import { Program } from '../swagger/models/Program'
import { useMemo } from 'react'
import { ProgramStatus, ProgramType } from '../swagger/models'

export type ProgramForSorting = Pick<
  Program,
  'type' | 'status' | 'programsKey' | 'hideFromPublicSearch'
>
export type ProgramForTypeSorting = Pick<Program, 'type' | 'programsKey'>

const ordinalMapOfProgramTypeEnum = new Map(
  Object.values(ProgramType).map((value, index) => {
    return [value, index]
  })
)

const ordinalMapOfProgramStatusEnum = new Map(
  Object.values(ProgramStatus).map((value, index) => {
    return [value, index]
  })
)

/**
 * Sorts an array of programs by their type and status, ensuring that programs
 * hidden from public search are placed at the end of the list.
 *
 * The sorting logic is as follows:
 * 1. Programs with `hideFromPublicSearch` set to `true` are moved to the end.
 * 2. Programs are grouped by their status using an ordinal mapping.
 * 3. Within the same status group, programs are further grouped by their type
 *    using an ordinal mapping.
 * 4. If both status and type are the same, programs are sorted by their `programsKey`.
 *
 * @template Program - A generic type extending `ProgramForSorting`.
 * @param programs - The array of programs to be sorted.
 * @returns The sorted array of programs.
 */
export const sortProgramsByTypeAndStatus = <Program extends ProgramForSorting>(
  programs: Program[]
): Program[] => {
  return programs.sort((a, b) => {
    if (a.hideFromPublicSearch && !b.hideFromPublicSearch) {
      return 1
    }
    if (!a.hideFromPublicSearch && b.hideFromPublicSearch) {
      return -1
    }

    const statusGrouping =
      (ordinalMapOfProgramStatusEnum.get(a.status) ?? 0) -
      (ordinalMapOfProgramStatusEnum.get(b.status) ?? 0)
    const typeGrouping =
      (ordinalMapOfProgramTypeEnum.get(a.type) ?? 0) -
      (ordinalMapOfProgramTypeEnum.get(b.type) ?? 0)
    return statusGrouping === 0
      ? typeGrouping === 0
        ? a.programsKey - b.programsKey
        : typeGrouping
      : statusGrouping
  })
}

export const sortProgramsByType = <Program extends ProgramForTypeSorting>(
  programs: Program[]
): Program[] => {
  return programs.sort((leftProgram, rightProgram) => {
    const programTypeOrder =
      (ordinalMapOfProgramTypeEnum.get(leftProgram.type) ??
        Number.MAX_SAFE_INTEGER) -
      (ordinalMapOfProgramTypeEnum.get(rightProgram.type) ??
        Number.MAX_SAFE_INTEGER)

    return programTypeOrder === 0
      ? leftProgram.programsKey - rightProgram.programsKey
      : programTypeOrder
  })
}
export const useSortedPrograms = (programs: Program[]): Program[] => {
  const communityProgramsByTypeAndStatus = useMemo(
    () => sortProgramsByTypeAndStatus(programs),
    [programs]
  )

  return communityProgramsByTypeAndStatus
}

export default useSortedPrograms
